import { type OfferCard } from '@/components/Shared/ui';
import { formatPrice } from '@/services/formatter';
import type {
  TAutoCredLeasingRequest,
  TAutoCredLeasingResponse
} from '@/types/newOrder';

import type { IAdditionalConditions } from '../types/intersecting.types';

type OfferBodyProductsParams = {
  monthlyPayment?: number | string;
  rate?: number | string;
  loanPeriod?: number | string;
  loanAmount?: number | string;
  initialPayment?: number | string;
  bankProduct?: string;
  isMobile: boolean;
  firstMonthlyPayment?: string;
  additionalConditions?: IAdditionalConditions;
};

export const getOfferBodyProducts = ({
  monthlyPayment,
  rate,
  loanPeriod,
  loanAmount,
  initialPayment,
  bankProduct,
  isMobile,
  firstMonthlyPayment,
  additionalConditions
}: OfferBodyProductsParams) => {
  const products = [
    {
      title: isMobile ? 'Платеж в мес.' : 'Ежемесячный платеж',
      productValue: monthlyPayment,
      subInto: firstMonthlyPayment
        ? {
            title: 'Первый платеж:',
            description: firstMonthlyPayment + ' ₸'
          }
        : undefined,
      descriptionFormatter: (value: string | number) =>
        `${formatPrice(value)} ₸`
    },
    {
      title: isMobile ? 'Ставка' : 'Процентная ставка',
      productValue: rate,
      descriptionFormatter: (value: string | number) => `${value}%`,
      isRed: true
    },
    {
      title: isMobile ? 'Срок' : 'Срок кредита',
      productValue: loanPeriod,
      descriptionFormatter: (value: string | number) => `${value} месяца`
    },
    ...(additionalConditions
      ? [
          {
            title: 'Платеж в мес. после льготного периода',
            productValue: additionalConditions.monthlyPayment,
            descriptionFormatter: (value: string | number) => `${value} ₸`
          },
          {
            title: 'Ставка после льготного периода',
            productValue: additionalConditions.rate,
            isRed: true,
            descriptionFormatter: (value: string | number) => `${value}%`
          },
          {
            title: isMobile ? 'Срок' : 'Срок кредита',
            productValue: additionalConditions.loanPeriod,
            descriptionFormatter: (value: string | number) => `${value} месяца`
          }
        ]
      : []),
    {
      title: 'Сумма займа',
      productValue: loanAmount,
      descriptionFormatter: (value: string | number) =>
        `${formatPrice(value)} ₸`
    },
    {
      title: 'Первоначальный взнос',
      productValue: initialPayment,
      descriptionFormatter: (value: string | number) =>
        `${formatPrice(value)} ₸`
    },
    {
      title: 'Продукт',
      productValue: bankProduct,
      descriptionFormatter: (value: string | number) => `${value}`
    }
  ];

  return products.map(
    (item) =>
      ({
        ...item,
        description:
          item.productValue != null
            ? item.descriptionFormatter(item.productValue)
            : ''
      }) satisfies React.ComponentProps<
        typeof OfferCard.Body
      >['products'][number] & {
        productValue: string | number | null | undefined;
        descriptionFormatter: (value: string | number) => string;
      }
  );
};

export const getLeasingOfferBodyProducts = ({
  leasingOffer,
  leasingOrder,
  mobileAlternatives
}: {
  leasingOffer: TAutoCredLeasingResponse;
  leasingOrder: TAutoCredLeasingRequest | null;
  mobileAlternatives: boolean;
}) => {
  const {
    balloonAmount,
    firstMonthPayment,
    rate,
    loanPeriod,
    approvedAmount,
    initialPayment
  } = leasingOffer;
  const { initialPayment: requestInitialPayment } = leasingOrder || {};

  const products = [
    {
      title: mobileAlternatives ? 'Платеж в мес.' : 'Ежемесячный платеж',
      productValue: firstMonthPayment ?? '-',
      descriptionFormatter: (value: string | number) =>
        `${formatPrice(value)} ₸`
    },
    {
      title: mobileAlternatives ? 'Ставка' : 'Процентная ставка',
      productValue: rate ?? '-',
      descriptionFormatter: (value: string | number) => `${value}%`,
      isRed: true
    },
    {
      title: mobileAlternatives ? 'Срок' : 'Срок лизинга',
      productValue: loanPeriod ?? '-',
      descriptionFormatter: (value: string | number) => `${value} месяца`
    },
    {
      title: 'Сумма займа',
      productValue: approvedAmount ?? '-',
      descriptionFormatter: (value: string | number) =>
        `${formatPrice(value)} ₸`
    },
    {
      title: 'Авансовый взнос',
      productValue: initialPayment ?? requestInitialPayment ?? '-',
      descriptionFormatter: (value: string | number) =>
        `${formatPrice(value)} ₸`
    },
    {
      title: 'Остаточная стоимость',
      productValue: balloonAmount ?? '-',
      descriptionFormatter: (value: string | number) =>
        `${formatPrice(value)} ₸`
    }
  ];

  return products.map(
    (item) =>
      ({
        ...item,
        description:
          item.productValue != null
            ? item.descriptionFormatter(item.productValue)
            : ''
      }) satisfies React.ComponentProps<
        typeof OfferCard.Body
      >['products'][number] & {
        productValue: string | number | null | undefined;
        descriptionFormatter: (value: string | number) => string;
      }
  );
};
